import { getCurrencySymbol, getLocaleCurrencyCode } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID } from '@angular/core';
import { Selection, SummarySectionElements } from 'src/app/interfaces/form-model';
import { CurrencyService } from 'src/app/service/currency.service';

@Component({
  selector: 'app-summary-price-field',
  templateUrl: './summary-price-field.component.html',
  styleUrls: ['./summary-price-field.component.scss']
})
export class SummaryPriceFieldComponent {

  @Input() element!: SummarySectionElements;

  constructor(
    private currencyService: CurrencyService,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  // Number-style price fields show an entered value; the rest show their selected options.
  // A Membership Options field (345) is number-style only when priced by a number attribute.
  isNumberPriceField(): boolean {
    if (this.element.type === 345) {
      const options = this.element.templateOptions;
      const pricingSchema = this.element.pricingSchema ?? options?.pricingSchema;
      const attributeTypeId = this.element.attributeTypeId ?? options?.attributeTypeId;
      return pricingSchema === 1 && attributeTypeId === 1;
    }

    return [2, 201].includes(this.element.type);
  }

  getSelectedElements() {
    return this.element?.selections?.filter((selection: Selection) => selection.selected) ?? [];
  }

  getSelectionName(selection: any) {
    return selection.name;
  }

  getOriginalPrice(selection: any): string {
    return this.currencyService.formatCurrency(parseFloat(selection.price));
  }

  getSelectionValue(selection: any) {
    // Check for admin override price first
    if (selection.displayAdminOverridePrice && selection.adminOverridePrice !== undefined) {
      return selection.adminOverridePrice;
    }

    // Check for discount price next
    if (selection.displayDiscountCodePrice && selection.discountCodePrice !== undefined) {
      return selection.discountCodePrice;
    }

    // Default to regular price
    return selection.price;
  }

  getSelectionPrice(selection: any): string {
    // Check for admin override price first
    if (selection.displayAdminOverridePrice && selection.adminOverridePrice !== undefined) {
      return this.currencyService.formatCurrency(parseFloat(selection.adminOverridePrice));
    }

    // Check for discount price next
    if (selection.displayDiscountCodePrice && selection.discountCodePrice !== undefined) {
      return this.currencyService.formatCurrency(parseFloat(selection.discountCodePrice));
    }

    // Default to regular price
    return this.currencyService.formatCurrency(parseFloat(selection.price));
  }

  getTextFromNumberWithCurrency() {
    return this.currencyService.formatCurrency(parseFloat(this.element.value));
  }

  getTextFromNumber() {
    return parseFloat(this.element.value);
  }

  getPriceMultiplied() {
    if(this.element.multiplier) {
      return this.currencyService.formatCurrency(parseFloat(this.element.value) * this.element.multiplier);
    }

    return this.currencyService.formatCurrency(parseFloat(this.element.value) * this.element.priceMultiplier);
  }

  getCurrencySymbol() {

    const currencyCode = getLocaleCurrencyCode(this.locale) || "en";

    return getCurrencySymbol(currencyCode, "narrow", this.locale);
  }

  onChange(event: Event, selection: number, element: SummarySectionElements) {

    const value = (event.target as HTMLInputElement).value;

    this.element.callback(value, selection, element);
  }
}
